/**
 * Shipping Rates API Hooks
 * React Query hooks for managing shipping rates
 */

import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { getShippingRates, getShippingRate, createShippingRate, updateShippingRate, deleteShippingRate } from "@/api/shipping-rates.api";
import type { CreateShippingRate, UpdateShippingRate } from "../types/shipping-rates.types";

const QUERY_KEY = ["shipping-rates"];

/**
 * Get all shipping rates
 */
export function useShippingRates() {
  return useQuery({
    queryKey: QUERY_KEY,
    queryFn: getShippingRates,
  });
}

/**
 * Get a single shipping rate by ID
 */
export function useShippingRate(id: string) {
  return useQuery({
    queryKey: [...QUERY_KEY, id],
    queryFn: () => getShippingRate(id),
    enabled: !!id,
  });
}

/**
 * Create a new shipping rate
 */
export function useCreateShippingRate() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreateShippingRate) => createShippingRate(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEY });
    },
  });
}

/**
 * Update an existing shipping rate
 */
export function useUpdateShippingRate(id: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: UpdateShippingRate) => updateShippingRate(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEY });
      queryClient.invalidateQueries({ queryKey: [...QUERY_KEY, id] });
    },
  });
}

/**
 * Delete a shipping rate
 */
export function useDeleteShippingRate() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => deleteShippingRate(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEY });
    },
  });
}
